import React, { useCallback, useContext, useEffect } from "react";
import { AuthContext } from "../auth/AuthContext";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { LoginScreen } from "./protectedRoute";
import { useQuery } from "@tanstack/react-query";
import { Box, CircularProgress, Typography } from "@mui/material";

// Where to send the user after login. Persisted while logged out so deep links
// (including the query string, e.g. /gpt-driver/tests?folder=abc) survive a
// login — even SAML, which round-trips through a full page reload.
const POST_LOGIN_REDIRECT_KEY = "postLoginRedirect";

const AuthenticatedRoute = ({ children }) => {
  const useAuth = () => useContext<any>(AuthContext);
  const { user, loading, loadingUser, signInWithToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const customAuthToken = searchParams.get("token");
  const autorunParam = searchParams.get("autorun");

  const navigateTo = useCallback(
    (path: string) => {
      navigate(path);
    },
    [navigate]
  );

  // While logged out, remember the full intended location; once logged in,
  // restore it (the email/Google in-place login already keeps the URL, so this
  // mainly recovers it after a SAML round-trip or a redirect that dropped it).
  useEffect(() => {
    if (loading || loadingUser || customAuthToken != null) return;
    const here = location.pathname + location.search;
    if (user?.uid == null) {
      sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, here);
      return;
    }
    const saved = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
    if (saved) {
      sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
      if (saved !== here) void navigate(saved, { replace: true });
    }
  }, [
    user?.uid,
    loading,
    loadingUser,
    customAuthToken,
    location.pathname,
    location.search,
    navigate,
  ]);

  useQuery({
    queryKey: ["signInWithCustomToken"],
    queryFn: async () => {
      await signInWithToken(customAuthToken);
      navigateTo(
        autorunParam === "true"
          ? `${location.pathname}?autorun=true`
          : location.pathname
      );
      return true;
    },
    enabled:
      user?.email == null && loadingUser === false && customAuthToken != null,
  });

  if (loading || loadingUser || customAuthToken != null) {
    return (
      <Box
        sx={{
          height: "100%",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          bgcolor: "white",
        }}
      >
        <CircularProgress />
        {customAuthToken != null && (
          <Typography variant="body2" color="text.secondary">
            Signing you in...
          </Typography>
        )}
      </Box>
    );
  }

  if (user.uid !== null) {
    return children;
  } else {
    return <LoginScreen />;
  }
};

export { AuthenticatedRoute };
